import {PageStandard} from "../utils/PageStandard";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {getFlows, getLiveMonitor, getUserLocations, resetBaseTimers, resetGameData, resetPoints, updateLastStep } from "../utils/ViewModel";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import L from "leaflet";
import {BaseFlowStep, Flow, FlowStep, FlowStepTypes, LiveMonitorModel, UserLocation} from "../utils/Model";
import {Button, Card, CardContent, CardOverflow, Divider, Grid, Sheet, Stack, Table, Typography } from "@mui/joy";
import moment from "moment";
import { formatDateTime, getUserTimezone } from "../utils/Utils";

export function LiveMonitorGame() {
    const { id } = useParams<{ id: string }>();
    const [locations, setLocations] = useState<UserLocation[]>([]);
    const [liveMonitor, setLiveMonitor] = useState<LiveMonitorModel[]>([]);
    const [flows, setFlows] = useState<Flow[]>([]);

    const handleGetFlows = () => {
        if(id) {
            getFlows(id)
                .then((flows) => {
                    if (flows) {
                        setFlows(flows);
                    }
                })
                .catch((err) => alert("Errore nel recupero dei flow: " + err.message));
        }
    };

    // Funzione per recuperare le posizioni dal server
    const fetchLocations = async () => {
        if (!id) return;
        const locs = await getUserLocations(id);
        if (locs) setLocations(locs);
    };
    const fetchLiveMonitor = async () => {
        if (!id) return;
        const res = await getLiveMonitor(id);
        console.log(res);
        if (res) setLiveMonitor(res);
    };

    // Prima fetch + interval ogni 10 secondi
    useEffect(() => {
        fetchLiveMonitor()
        handleGetFlows()
    }, []);

    // Prima fetch + interval ogni 10 secondi
    useEffect(() => {
        fetchLocations(); // fetch iniziale
        const interval = setInterval(fetchLocations, 10000); // refresh ogni 10s
        return () => clearInterval(interval); // cleanup
    }, [id]);

    // Funzione per creare un marker personalizzato con cerchio + iniziale
    const createMarkerIcon = (name: string) => {
        const initial = name.charAt(0).toUpperCase();
        return L.divIcon({
            html: `<div style="
                background-color: rgba(0, 123, 255, 0.5);
                width: 40px;
                height: 40px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-weight: bold;
                font-size: 16px;
                border: 2px solid white;
            ">${initial}</div>`,
            className: "",
            iconSize: [40, 40],
            iconAnchor: [20, 20],
        });
    };

    const calculateProgress = (lastStep: number, totalSteps: number): number => {
        if (totalSteps === 0) return 0;
        return Math.round((lastStep / totalSteps) * 100);
    };

    const formatMinutesToTime = (minutes: number): string => {
        const mins = Math.floor(minutes);
        const secs = Math.floor((minutes - mins) * 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const calculateTimerDisplayed = (baseTimer: string, completeUntil: number): string => {
        if (!baseTimer) return '--:--';

        const baseDate = new Date(baseTimer);
        const targetDate = new Date(baseDate.getTime() + completeUntil * 60 * 1000);
        const now = new Date();

        const diffMs = targetDate.getTime() - now.getTime();

        if (diffMs <= 0) return '00:00';

        const minutes = Math.floor(diffMs / (1000 * 60));
        const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleResetBaseTimers = (experienceId: string) => {
        resetBaseTimers(experienceId)
            .then((ok) => {
                if (ok) {
                    window.alert("BASE_TIMER RESETTATI")
                }
            })
            .catch((err) => alert("Errore durante il reset dei base timers: " + err.message));
    };
    const handleResetPoints = (experienceId: string) => {
        resetPoints(experienceId)
            .then((ok) => {
                if (ok) {
                    window.alert("POINTS RESETTATI")
                }
            })
            .catch((err) => alert("Errore durante il reset dei punti: " + err.message));
    };
    const handleResetGameData = (experienceId: string) => {
        resetGameData(experienceId)
            .then((ok) => {
                if (ok) {
                    window.alert("DATI DI GIOCO RESETTATI")
                }
            })
            .catch((err) => alert("Errore durante il reset dei dati di gioco: " + err.message));
    };

    return (
        <PageStandard title="Live Monitor" sidebarVisible={true} headerEnd={
            <Stack direction="row" spacing={2}>
                <Button variant="outlined" color="danger"
                        onClick={() => {
                            if(window.confirm("Vuoi resettare base_timer?") && id) {
                                handleResetBaseTimers(id)
                            }
                        }}
                >
                    Reset BASE_TIMER
                </Button>
                <Button variant="outlined" color="danger"
                        onClick={() => {
                            if(window.confirm("Vuoi resettare points?") && id) {
                                handleResetPoints(id)
                            }
                        }}
                >
                    Reset POINTS
                </Button>
                <Button variant="solid" color="danger"
                        onClick={() => {
                            if(window.confirm("Vuoi resettare tutti i dati di gioco (punteggi, base_timer, step, token)? Flow, partecipazioni e task NON verranno eliminati.") && id) {
                                handleResetGameData(id)
                            }
                        }}
                >
                    Reset dati di gioco
                </Button>
            </Stack>
        }>
            {
                liveMonitor.length > 0 ?
                    <Card variant="outlined">
                        <CardOverflow variant="soft">
                            <CardContent sx={{textAlign: "center"}}>
                                <Typography level="title-md" fontWeight="bold">Info gruppi</Typography>
                            </CardContent>
                            <Divider inset="context" />
                        </CardOverflow>
                        <CardContent>
                            <Table size="sm">
                                <thead>
                                <tr>
                                    <th></th>
                                    {liveMonitor.map((flow, index) => (
                                        <th key={`header-${flow.flowId}-${index}`}>{flow.flowTitle}</th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td>Leader</td>
                                    {liveMonitor.map((flow, index) => (
                                        <td key={`leader-${flow.flowId}-${index}`}>
                                            <b>{flow.leader}</b>
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td>Partecipanti</td>
                                    {liveMonitor.map((flow, index) => (
                                        <td key={`participants-${flow.flowId}-${index}`}>
                                            <b>{flow.participations}</b>
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td>Last step</td>
                                    {liveMonitor.map((flow, index) => (
                                        <td key={`laststep-${flow.flowId}-${index}`}>
                                            <b>{flow.lastStep}/{flow.totalSteps}</b>
                                            <br/>
                                            <Stack direction="row" spacing={1}>
                                                <button
                                                    onClick={() => {
                                                        if(window.confirm("Andare allo step successivo?")) {
                                                            goToNextStep(flow)
                                                        }
                                                    }}
                                                >
                                                    +
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if(window.confirm("Andare allo step precedente?")) {
                                                            goToPrevStep(flow)
                                                        }
                                                    }}
                                                >
                                                    -
                                                </button>
                                            </Stack>
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td>Last semaforo</td>
                                    {liveMonitor.map((flow, index) => (
                                        <td key={`lastsemaforo-${flow.flowId}-${index}`}>
                                            <b>{flow.lastSemaforo}/{flow.totalSemaforo}</b>
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td>Progress</td>
                                    {liveMonitor.map((flow, index) => (
                                        <td key={`progress-${flow.flowId}-${index}`}>
                                            <b>{calculateProgress(flow.lastStep, flow.totalSteps)}%</b>
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td colSpan={liveMonitor.length + 1}>
                                        <b>CURRENT STEP</b>
                                    </td>
                                </tr>
                                <tr>
                                    <td>Type</td>
                                    {liveMonitor.map((flow, index) => (
                                        <td key={`type-${flow.flowId}-${index}`}>
                                            <b>{flow.currentStep.type}</b>
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td>Display title</td>
                                    {liveMonitor.map((flow, index) => (
                                        <td key={`title-${flow.flowId}-${index}`}>
                                            <b>{flow.currentStep.displayTitle || '-'}</b>
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td>Base timer</td>
                                    {liveMonitor.map((flow, index) => {
                                        const baseTimerDate = moment(flow.baseTimer)
                                        const diffMin = moment().diff(baseTimerDate, "minutes")
                                        const diffSec = moment().diff(baseTimerDate, "seconds") % 60
                                        return (
                                            <td key={`basetimer-${flow.flowId}-${index}`}>
                                                <b>{baseTimerDate.format("DD/MM/YYYY HH:mm:ss")} ({getUserTimezone()})</b>
                                                <br/>
                                                <b>Tempo passato: {diffMin}:{diffSec}</b>
                                            </td>
                                        )
                                    })}
                                </tr>
                                <tr>
                                    <td>Complete until sum</td>
                                    {liveMonitor.map((flow, index) => (
                                        <td key={`completeuntil-${flow.flowId}-${index}`}>
                                            <b>{formatMinutesToTime(flow.completeUntil)}</b>
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td>Timer displayed</td>
                                    {liveMonitor.map((flow, index) => (
                                        <td key={`timerdisplayed-${flow.flowId}-${index}`}>
                                            <b>{calculateTimerDisplayed(flow.baseTimer, flow.completeUntil)}</b>
                                        </td>
                                    ))}
                                </tr>
                                </tbody>
                            </Table>
                        </CardContent>
                    </Card>
                    : null
            }

            <div style={{height: "70vh"}}>
                <MapContainer
                    center={[45.07049, 7.68682]}
                    zoom={16}
                    scrollWheelZoom={true}
                    className="map-container-leaflet"
                    style={{height: "100%", width: "100%"}}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    {locations.map((user) => (
                        <CircleMarker
                            key={user.id}
                            center={[user.latitude, user.longitude]}
                            radius={20}
                            pathOptions={{color: "blue", fillColor: "blue", fillOpacity: 0.5}}
                        >
                            <Popup>
                                <div style={{minWidth: 150}}>
                                    <strong>{user.name}</strong>
                                    <br/>
                                    Lat: {user.latitude.toFixed(5)}, Lng: {user.longitude.toFixed(5)}
                                    <br/>
                                    Aggiornato: {formatDateTime(user.date)}
                                </div>
                            </Popup>
                        </CircleMarker>
                    ))}
                </MapContainer>
            </div>
        </PageStandard>
    )

    function goToNextStep(flow: LiveMonitorModel) {
        const index = flow.lastStep + 1

        const steps = flows.filter(f => f.id === flow.flowId)[0].steps
        const isSemaforo = steps[index].type === FlowStepTypes.SEMAFORO
        const lastSemaforoIndex = getLastSemaforoIndex(steps, flow.lastStep)
        const semaforoParam = isSemaforo ? (lastSemaforoIndex + 1).toString() : "null"

        updateLastStep(flow.participationId, index, flow.flowId, semaforoParam)
            .then((ok) => {
                if (ok) {
                    fetchLiveMonitor()
                }
            })
            .catch((err) =>
                alert("Errore durante l'aggiornamento dell'ultimo step: " + err.message)
            );
    }
    function goToPrevStep(flow: LiveMonitorModel) {
        const index = flow.lastStep - 1

        const steps = flows.filter(f => f.id === flow.flowId)[0].steps
        const isSemaforo = steps[index].type === FlowStepTypes.SEMAFORO
        const lastSemaforoIndex = getLastSemaforoIndex(steps, flow.lastStep)
        const semaforoParam = isSemaforo ? (lastSemaforoIndex + 1).toString() : "null"

        updateLastStep(flow.participationId, index, flow.flowId, semaforoParam)
            .then((ok) => {
                if (ok) {
                    fetchLiveMonitor()
                }
            })
            .catch((err) =>
                alert("Errore durante l'aggiornamento dell'ultimo step: " + err.message)
            );
    }

    function getLastSemaforoIndex(steps: FlowStep[], lastStepIndex: number): number {

        // Filtra gli step di tipo "SEMAFORO" che sono prima dell'indice lastStepIndex (escluso)
        const semaforiPrecedenti = steps.slice(0, lastStepIndex).filter(step => step.type === FlowStepTypes.SEMAFORO);

        // Ritorna la lunghezza dell'array filtrato
        return semaforiPrecedenti.length;
    }

}